import React from 'react'
import { Modal, Select, Spin, Table, Typography, message } from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { productApi } from 'api/product'
import {
  EPriceChangeSource,
  EProductType,
  IPriceHistoryItem,
  IPriceHistoryResponse
} from 'models/product'
import { getNormalizedPrice } from 'shared/utils/getNormalizedPrice'
import { PriceHistoryChart } from './PriceHistoryChart'
import styles from './PriceHistory.module.scss'

const SOURCE_LABELS: Record<EPriceChangeSource, string> = {
  [EPriceChangeSource.SELLER]: 'Кабинет продавца',
  [EPriceChangeSource.ADMIN]: 'Администратор',
  [EPriceChangeSource.EXCEL_IMPORT]: 'Импорт Excel',
  [EPriceChangeSource.INSALES]: 'Синхронизация InSales',
  [EPriceChangeSource.MOYSKLAD]: 'Синхронизация МойСклад',
  [EPriceChangeSource.ONE_C]: 'Синхронизация 1С',
  [EPriceChangeSource.API]: 'API продавца',
  [EPriceChangeSource.DISCOUNT]: 'Скидка',
  [EPriceChangeSource.SYSTEM]: 'Система'
}

const formatPrice = (price: number | null) =>
  price == null ? '—' : getNormalizedPrice(price)

const formatChange = (prev: number | null, next: number | null) =>
  `${formatPrice(prev)} → ${formatPrice(next)}`

const getAuthor = ({ author, source }: IPriceHistoryItem) => {
  if (author) return author.name || author.email || '—'
  return source === EPriceChangeSource.ADMIN ? 'Администрация HorseSmart' : '—'
}

const columns: ColumnsType<IPriceHistoryItem> = [
  {
    title: 'Дата',
    dataIndex: 'createdAt',
    width: 140,
    render: (createdAt: string) => dayjs(createdAt).format('DD.MM.YYYY HH:mm')
  },
  {
    title: 'Цена',
    render: (_, item) => (
      <>
        <div className={styles.price}>
          {item.old_price == null
            ? `${formatPrice(item.new_price)} — при создании`
            : formatChange(item.old_price, item.new_price)}
        </div>
        {item.old_f_price !== item.new_f_price && (
          <Typography.Text type='secondary'>
            До скидки: {formatChange(item.old_f_price, item.new_f_price)}
          </Typography.Text>
        )}
      </>
    )
  },
  {
    title: 'Источник',
    render: (_, item) => (
      <>
        <div>{SOURCE_LABELS[item.source] ?? item.source}</div>
        {item.note && (
          <Typography.Text type='secondary'>«{item.note}»</Typography.Text>
        )}
      </>
    )
  },
  {
    title: 'Кто',
    render: (_, item) => getAuthor(item)
  }
]

interface IPriceHistoryModalProps {
  // товар, с которого открыли журнал: он выбран первым, остальная карточка — в списке
  productId: string | null
  onClose: () => void
}

export const PriceHistoryModal: React.FC<IPriceHistoryModalProps> = ({
  productId,
  onClose
}) => {
  const [history, setHistory] = React.useState<IPriceHistoryResponse | null>(null)
  const [selectedId, setSelectedId] = React.useState<string | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)

  React.useEffect(() => {
    if (!productId) return
    let isActual = true
    setHistory(null)
    setSelectedId(productId)
    setIsLoading(true)
    productApi
      .getPriceHistory(productId)
      .then(({ data }) => isActual && setHistory(data))
      .catch(() => message.error('Не удалось загрузить историю изменений стоимости'))
      .finally(() => isActual && setIsLoading(false))
    return () => {
      isActual = false
    }
  }, [productId])

  const selectedProduct = history?.products.find(
    (product) => product._id === selectedId
  )
  const selectedItems = React.useMemo(
    () => history?.items.filter((item) => item.product_id === selectedId) ?? [],
    [history, selectedId]
  )

  const productOptions = history?.products.map((product) => ({
    value: product._id,
    label:
      product.type === EProductType.PRODUCT
        ? `${product.name} — основной товар`
        : product.name
  }))

  return (
    <Modal
      title='История изменений стоимости'
      open={!!productId}
      onCancel={onClose}
      footer={null}
      width={860}
      destroyOnClose
    >
      <Spin spinning={isLoading}>
        {productOptions && productOptions.length > 1 && (
          <Select
            className={styles.productSelect}
            value={selectedId}
            options={productOptions}
            onChange={setSelectedId}
          />
        )}
        {selectedProduct && (
          <PriceHistoryChart product={selectedProduct} items={selectedItems} />
        )}
        <Table
          rowKey='_id'
          size='small'
          columns={columns}
          dataSource={selectedItems}
          pagination={{ pageSize: 20, hideOnSinglePage: true }}
          scroll={{ x: 640 }}
          locale={{ emptyText: 'Изменений цены пока нет' }}
        />
      </Spin>
    </Modal>
  )
}
